import { useState } from 'react';
import useSWR from 'swr';
import { adminApi, type ServiceInput } from '../../api/admin';
import type { EnvName, Host, Service, ServiceKind } from '../../api/types';

const EMPTY: ServiceInput = {
  id: '',
  host_id: '',
  env: 'dev',
  name: '',
  kind: 'app',
  enabled: true,
  accept_redirects: false,
};

export function ServicesTab() {
  const { data: services = [], mutate } = useSWR('admin/services', () => adminApi.listServices());
  const { data: hosts = [] } = useSWR<Host[]>('admin/hosts', () => adminApi.listHosts());

  const [editing, setEditing] = useState<ServiceInput | null>(null);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(s: ServiceInput) {
    setErr(null);
    try {
      if (adding) await adminApi.createService(s);
      else await adminApi.updateService(s);
      await mutate();
      setEditing(null);
      setAdding(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(s: Service) {
    if (!confirm(`Delete service '${s.id}'?`)) return;
    setErr(null);
    try {
      await adminApi.deleteService(s.id);
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-cockpit-dim">
          Services ({services.length})
        </h3>
        <button
          type="button"
          className="rounded border border-cockpit-border px-3 py-1 text-xs hover:bg-cockpit-muted"
          onClick={() => {
            setAdding(true);
            setEditing({ ...EMPTY, host_id: hosts[0]?.id ?? '' });
          }}
        >
          + Add service
        </button>
      </div>
      {err && (
        <div className="rounded border border-cockpit-crit bg-cockpit-crit/10 p-2 text-xs text-cockpit-crit">
          {err}
        </div>
      )}

      <div className="overflow-hidden rounded border border-cockpit-border">
        <table className="w-full text-sm">
          <thead className="bg-cockpit-panel text-xs uppercase tracking-wider text-cockpit-dim">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Host</th>
              <th className="px-3 py-2 text-left">Env</th>
              <th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">Port</th>
              <th className="px-3 py-2 text-left">NSSM</th>
              <th className="px-3 py-2 text-left">Health</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id} className="border-t border-cockpit-border">
                <td className="px-3 py-2 font-mono text-xs">{s.id}</td>
                <td className="px-3 py-2">{s.name}</td>
                <td className="px-3 py-2 text-xs">{s.host_id}</td>
                <td className="px-3 py-2 text-xs uppercase">{s.env}</td>
                <td className="px-3 py-2 text-xs">{s.kind}</td>
                <td className="px-3 py-2 text-xs">{s.port ?? '—'}</td>
                <td className="px-3 py-2 text-xs font-mono">{s.nssm_service ?? '—'}</td>
                <td className="px-3 py-2 text-xs">{s.health_url ?? '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    className="mr-2 text-xs text-cockpit-dim hover:text-cockpit-text"
                    onClick={() => {
                      setAdding(false);
                      setEditing({
                        ...s,
                        nssm_service: s.nssm_service ?? undefined,
                        port: s.port ?? undefined,
                        health_url: s.health_url ?? undefined,
                        accept_redirects: s.accept_redirects ?? false,
                      });
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-xs text-cockpit-crit hover:underline"
                    onClick={() => void remove(s)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {services.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-sm text-cockpit-dim">
                  no services yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ServiceForm
          service={editing}
          hosts={hosts}
          isNew={adding}
          onSave={save}
          onCancel={() => {
            setEditing(null);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

function ServiceForm({
  service,
  hosts,
  isNew,
  onSave,
  onCancel,
}: {
  service: ServiceInput;
  hosts: Host[];
  isNew: boolean;
  onSave: (s: ServiceInput) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState(service);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
      className="space-y-3 rounded border border-cockpit-border bg-cockpit-panel p-4"
    >
      <h4 className="text-sm font-semibold">{isNew ? 'New service' : `Edit ${service.id}`}</h4>
      <div className="grid grid-cols-2 gap-3 text-xs lg:grid-cols-3">
        <Field label="ID (immutable)" htmlFor="id">
          <input
            id="id"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text"
            value={form.id}
            disabled={!isNew}
            onChange={(e) => setForm({ ...form, id: e.target.value })}
            placeholder="bdxlens"
            required
          />
        </Field>
        <Field label="Display name" htmlFor="name">
          <input
            id="name"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </Field>
        <Field label="Host" htmlFor="host_id">
          <select
            id="host_id"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.host_id}
            onChange={(e) => setForm({ ...form, host_id: e.target.value })}
            required
          >
            <option value="" disabled>
              — pick a host —
            </option>
            {hosts.map((h) => (
              <option key={h.id} value={h.id}>
                {h.id} ({h.env})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Environment" htmlFor="env">
          <select
            id="env"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.env}
            onChange={(e) => setForm({ ...form, env: e.target.value as EnvName })}
          >
            <option value="dev">dev</option>
            <option value="staging">staging</option>
            <option value="prod">prod</option>
          </select>
        </Field>
        <Field label="Kind" htmlFor="kind">
          <select
            id="kind"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value as ServiceKind })}
          >
            <option value="app">app</option>
            <option value="database">database</option>
            <option value="tunnel">tunnel</option>
            <option value="os-service">os-service</option>
          </select>
        </Field>
        <Field label="Port" htmlFor="port">
          <input
            id="port"
            type="number"
            min={1}
            max={65535}
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.port ?? ''}
            onChange={(e) =>
              setForm({ ...form, port: e.target.value ? Number(e.target.value) : undefined })
            }
          />
        </Field>
        <Field label="NSSM service name" htmlFor="nssm_service">
          <input
            id="nssm_service"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text"
            value={form.nssm_service ?? ''}
            onChange={(e) => setForm({ ...form, nssm_service: e.target.value || undefined })}
            placeholder="QEA_BDXLens_Backend"
          />
        </Field>
        <Field label="Health URL" htmlFor="health_url" colSpan={2}>
          <input
            id="health_url"
            type="url"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.health_url ?? ''}
            onChange={(e) => setForm({ ...form, health_url: e.target.value || undefined })}
            placeholder="http://127.0.0.1:3020/health"
          />
        </Field>
        <label className="col-span-3 inline-flex items-center gap-2 text-cockpit-dim">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          <span>enabled (collectors poll only enabled services)</span>
        </label>
        <label className="col-span-3 inline-flex items-center gap-2 text-cockpit-dim">
          <input
            type="checkbox"
            checked={form.accept_redirects}
            onChange={(e) => setForm({ ...form, accept_redirects: e.target.checked })}
          />
          <span>
            treat 3xx redirects as healthy (for apps whose /health redirects to a login page, e.g.
            QEA-Dex)
          </span>
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" className="rounded bg-cockpit-text px-3 py-1 text-xs text-cockpit-bg">
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-cockpit-border px-3 py-1 text-xs text-cockpit-dim hover:text-cockpit-text"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  children,
  colSpan = 1,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
  colSpan?: 1 | 2 | 3;
}) {
  const cls =
    colSpan === 3
      ? 'col-span-3 flex flex-col gap-1'
      : colSpan === 2
        ? 'col-span-2 flex flex-col gap-1'
        : 'flex flex-col gap-1';
  return (
    <label htmlFor={htmlFor} className={cls}>
      <span className="text-cockpit-dim">{label}</span>
      {children}
    </label>
  );
}
